// Kitchen stations (#303): which list the page is about, and what the person looking may do
// with it. A venue's manager sets up their kitchen; the server gates each key it is sent
// (`kitchen.configure` for the stations, `settings.edit` for the money stored beside them),
// and this keeps the page from offering what that would refuse.

/** Does any site run its own stations? That is "Each site its own"; otherwise every site
 *  uses the business's one list. */
export const eachSiteItsOwn = (cfg) => Object.values(cfg?.venues || {}).some((v) => v && v.kitchen_stations != null);

/**
 * The site the page reads and saves: the one picked in the header, or the only one this
 * person can see. Most managers see one site of a group, and the header has no switcher
 * below two, so `site` is null for them; reading that as "the business" showed them the
 * business's list and saved theirs to a site called `null`, which the server refused.
 */
export const stationsSite = (site, sites) => site || ((sites || []).length === 1 ? sites[0].id : null);

/**
 * What the page shows this person.
 *
 * - `here`: the site it loads and saves, or null for the business's list.
 * - `needsSite`: each site runs its own and none is chosen, so there is nothing honest to show.
 * - `canEdit`: they hold `kitchen.configure`. Without it the page is read-only.
 * - `mode`: how "Same at every site / Each site its own" appears. `'switch'` only for someone
 *   who can edit and whose access covers every site of the business, because the switch
 *   rewrites every site's own list (`covers_every_site`, from the server: the site list here
 *   is only the ones this person can see). `'words'` for anyone else who sees more than one
 *   site, and for anyone at all on the shared list who doesn't cover every site. `null`
 *   otherwise: a single-site business never sees the choice, nor does someone at one site
 *   that runs its own list.
 * - `everywhere`: the business shares one list and this person doesn't see every site that
 *   uses it, so saving changes the stations at sites they can't see (#303 review). Most
 *   managers are here: one site in a group, no switcher, and a Save that re-routes the
 *   whole group's tickets.
 */
export function kitchenView({ cfg, site, sites, canConfigure }) {
  const perSite = eachSiteItsOwn(cfg);
  const here = stationsSite(site, sites);
  const multi = (sites || []).length > 1;
  const everywhere = !perSite && cfg?.covers_every_site === false;
  let mode = null;
  if (multi) mode = canConfigure && cfg?.covers_every_site === true ? 'switch' : 'words';
  else if (everywhere) mode = 'words';
  return {
    perSite,
    here,
    needsSite: perSite && !here,
    canEdit: !!canConfigure,
    mode,
    everywhere,
  };
}

/** What Save sends: this site's own list when each site has its own, else the business's. */
export function saveBody({ perSite, here, stations, routes }) {
  return perSite
    ? { venues: { [here]: { kitchen_stations: stations, kitchen_routes: routes } } }
    : { default_kitchen_stations: stations, default_kitchen_routes: routes };
}
